import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, History, CircleDot } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { describeChanges } from '../../services/git/changeItems';
import { gitActions } from '../../services/git/gitActions';
import { useGitSyncStore } from '../../store/gitSyncStore';
import { gitWords } from '../../services/git/gitSyncStatus';
import { whenLabel, initials } from './GitHistory';
import GitChangeRow from './GitChangeRow';
import GitPutBackButton from './GitPutBackButton';
import './GitView.css';
import './ItemHistory.css';

/** The dot's colour: created · changed · renamed / moved · deleted. */
function toneOf(v) {
  if (v.renamedFrom) return 'renamed';
  if (v.kind === 'added') return 'added';
  if (v.kind === 'deleted') return 'deleted';
  return 'modified';
}

function headline(v) {
  if (v.renamedFrom) {
    // A new name, or the same name in another collection or folder.
    return v.oldName && v.oldName !== v.newName ? `Renamed (was “${v.oldName}”)` : 'Moved';
  }
  if (v.kind === 'added') return 'Created';
  if (v.kind === 'deleted') return 'Deleted';
  return 'Changed';
}

/** One sync of this item: opened, what it changed (field by field) and "Restore this version". */
function VersionRow({ version, fileAt, onRestore, busy, layout, words }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // { item, file } | { error }

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || state) return;
    try {
      const before = version.kind === 'added' ? null : await fileAt(version.parent, version.beforePath);
      const file = { path: version.path, kind: version.kind, before: before ?? null, after: version.after };
      setState({ item: { id: version.oid, ...describeChanges([file], layout)[0] }, file });
    } catch (e) {
      setState({ error: e?.message || String(e) });
    }
  };

  const content = version.after ?? null;
  // A point on the timeline (redesign 05.10.2026, owner: "like a table but no
  // table"): a dot coloured by what happened, the headline, when and who;
  // opened, the change field by field and Restore.
  return (
    <li className={`ih-version ih-version--${toneOf(version)}${open ? ' ih-version--open' : ''}`} data-testid="item-version">
      <span className="ih-dot" aria-hidden />
      <button type="button" className="ih-head" aria-expanded={open} onClick={toggle}>
        <span className="ih-what">{headline(version)}</span>
        <span className="ih-meta">
          <span className="ih-when">{whenLabel(version.time)}</span>
          {version.author && (
            <span className="ih-who">
              <span className="git-av" aria-hidden>
                {initials(version.author)}
              </span>
              {version.author}
            </span>
          )}
        </span>
        <ChevronRight size={14} className={`ih-chevron${open ? ' ih-chevron--open' : ''}`} aria-hidden />
      </button>
      {open && (
        <div className="ih-body">
          {!state && (
            <p className="git-empty git-loading" role="status">
              <span className="dbm-spinner" aria-hidden /> Loading this version…
            </p>
          )}
          {state?.error && <p className="git-row-error">{state.error}</p>}
          {state?.item && (
            <GitChangeRow
              item={state.item}
              file={state.file}
              actions={
                content != null && (
                  <GitPutBackButton item={state.item} mode="restore" words={words} disabled={busy} onConfirm={() => onRestore(content)} />
                )
              }
            />
          )}
        </div>
      )}
    </li>
  );
}

/**
 * A request's git history in place (git sync card 9): the syncs that changed
 * it — renames followed — newest first, each with the same diff as the Git
 * page and "Restore this version". Restoring writes the request's CURRENT
 * file, so an old name comes back as the same request, never a copy.
 */
/**
 * `source` (boards & documents, git sync S2.7): { load(), restore(path, content),
 * noun: 'page' | 'task', layout: 'scope' } in place of a workspace.
 */
export default function ItemHistoryModal({ isOpen, onClose, workspace, itemId, itemName, source }) {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  // While this workspace syncs, opening History would rewrite the mirror under a
  // pull or merge: it waits, and Restore is off (as on the Git page).
  const syncing = useGitSyncStore((s) => !source && s.syncingId != null && String(s.syncingId) === String(workspace?.id));
  const noun = source?.noun || 'request';
  // Boards & documents (`source`) always sync; a workspace may be tracked on this device only.
  // A source brings its own words (a scope tracked on this device saves versions, T10).
  const words = source?.words || gitWords(source ? null : workspace);
  const workspaceId = workspace?.id;

  const load = useCallback(async () => {
    if (source) {
      setState({ loading: true });
      try {
        setState({ loading: false, ...(await source.load()) });
      } catch (e) {
        setState({ loading: false, error: e?.message || String(e) });
      }
      return;
    }
    const { itemHistory } = gitActions();
    if (!itemHistory) {
      setState({ loading: false, error: 'Git is not available here.' });
      return;
    }
    setState({ loading: true });
    try {
      setState({ loading: false, ...(await itemHistory(workspace, itemId)) });
    } catch (e) {
      setState({ loading: false, error: e?.message || String(e) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by id: a new workspace object with the same id is the same history
  }, [workspaceId, itemId]);

  useEffect(() => {
    if (isOpen && !syncing) load();
  }, [isOpen, syncing, load]);

  const restore = async (content) => {
    setBusy(true);
    try {
      if (source) await source.restore(state.path, content);
      else await gitActions().putBack(workspace, state.path, content, 'restore');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const versions = state.versions || [];
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="History"
      subtitle={itemName || noun.charAt(0).toUpperCase() + noun.slice(1)}
      customIcon={<History size={22} />}
      size="2xl"
    >
      <div className="ih" data-testid="item-history">
        {syncing && <p className="ih-empty">{words.busy}</p>}
        {state.loading && !syncing && (
          <p className="ih-empty git-loading" role="status">
            <span className="dbm-spinner" aria-hidden /> Loading the history…
          </p>
        )}
        {state.error && <p className="git-row-error">{state.error}</p>}
        {!state.loading && !state.error && !state.path && <p className="ih-empty">{words.itemNotYet(noun)}</p>}
        {state.unsynced && (
          <p className="ih-note" data-testid="item-unsynced">
            <CircleDot size={14} aria-hidden />
            {words.itemUnsaved(noun)}
          </p>
        )}
        {!state.loading && state.path && versions.length === 0 && <p className="ih-empty">{words.itemNone(noun)}</p>}
        {versions.length > 0 && (
          <ol className="ih-timeline">
            {versions.map((v) => (
              <VersionRow key={v.oid} version={v} fileAt={state.fileAt} onRestore={restore} busy={busy || syncing} layout={source?.layout} words={words} />
            ))}
          </ol>
        )}
      </div>
    </Modal>
  );
}
